/*
 * Утилиты форматирования транзакций
 *
 * Общие хелперы для отображения статусов, методов оплаты, валют
 * и временных меток транзакций. Используются в AdminTransactions и AdminUsers.
 */

// Перечисление статусов оплаты из сгенерированных proto-стабов
import { PaymentStatus } from '@vpn-tg/vue-client-grpc/proto/enums/transactionStatus'
// Перечисление методов оплаты из сгенерированных proto-стабов
import { PaymentMethod } from '@vpn-tg/vue-client-grpc/proto/enums/paymentMethod'
// Перечисление валют из сгенерированных proto-стабов
import { Currency } from '@vpn-tg/vue-client-grpc/proto/enums/currency'

// Доступные статусы оплаты для мультиселекта фильтрации
export const statusOptions = [
  { title: 'Ожидает', value: PaymentStatus.PaymentPending },
  { title: 'Отменена', value: PaymentStatus.PaymentCancelled },
  { title: 'Завершена', value: PaymentStatus.PaymentCompleted },
  { title: 'Возврат', value: PaymentStatus.PaymentRefunded },
]

// Временные статусы транзакций (по датам start..end)
export type TimeStatusValue = 'current' | 'future' | 'past'

// Доступные временные статусы для мультиселекта фильтрации
export const timeStatusOptions = [
  { title: 'Текущая', value: 'current' as TimeStatusValue },
  { title: 'Будущая', value: 'future' as TimeStatusValue },
  { title: 'Прошлая', value: 'past' as TimeStatusValue },
]

/**
 * Определяет временной статус транзакции по вхождению текущей даты в диапазон start..end
 *
 * @param {object} tx - Транзакция с опциональными dateTimeStart и dateTimeEnd
 * @returns {{ label: string, color: string, value: TimeStatusValue | '' }} Метка, цвет и значение для фильтрации
 */
export const getTimeStatus = (tx: {
  dateTimeStart?: { seconds: bigint | string | number; nanos: number }
  dateTimeEnd?: { seconds: bigint | string | number; nanos: number }
}): { label: string; color: string; value: TimeStatusValue | '' } => {
  if (!tx.dateTimeStart || !tx.dateTimeEnd) return { label: '', color: '', value: '' }

  const now = Date.now()
  const start = Number(tx.dateTimeStart.seconds) * 1000
  const end = Number(tx.dateTimeEnd.seconds) * 1000

  if (now >= start && now <= end) return { label: 'Текущая', color: 'success', value: 'current' }
  if (now < start) return { label: 'Будущая', color: 'info', value: 'future' }
  return { label: 'Прошлая', color: 'secondary', value: 'past' }
}

/**
 * Возвращает человекочитаемое название статуса оплаты
 *
 * @param {PaymentStatus} status - Числовой статус оплаты
 * @returns {string} Название статуса на русском языке
 */
export const getStatusLabel = (status: PaymentStatus): string => {
  const found = statusOptions.find((s) => s.value === status)
  return found ? found.title : `Статус ${status}`
}

/**
 * Возвращает цвет чипа для статуса оплаты
 *
 * @param {PaymentStatus} status - Числовой статус оплаты
 * @returns {string} Название цвета Vuetify
 */
export const getStatusColor = (status: PaymentStatus): string => {
  switch (status) {
    case PaymentStatus.PaymentPending:
      return 'warning'
    case PaymentStatus.PaymentCancelled:
      return 'error'
    case PaymentStatus.PaymentCompleted:
      return 'success'
    case PaymentStatus.PaymentRefunded:
      return 'info'
    default:
      return 'secondary'
  }
}

/**
 * Возвращает название метода оплаты
 *
 * @param {PaymentMethod} method - Числовой метод оплаты
 * @returns {string} Название метода
 */
export const getPaymentMethodLabel = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.YooKassa:
      return 'ЮKassa'
    case PaymentMethod.Manual:
      return 'Вручную'
    case PaymentMethod.ReferralSystem:
      return 'Реферальный бонус'
    default:
      return 'Неизвестно'
  }
}

/**
 * Возвращает название валюты
 *
 * @param {Currency} currency - Числовой код валюты
 * @returns {string} Краткое обозначение валюты
 */
export const getCurrencyLabel = (currency: Currency): string => {
  switch (currency) {
    case Currency.RUB:
      return 'RUB'
    case Currency.USD:
      return 'USD'
    default:
      return '—'
  }
}

/**
 * Форматирует proto Timestamp в читаемую дату в русской локали
 *
 * @param {object | undefined} ts - Объект Timestamp с полем seconds
 * @returns {string} Отформатированная дата или прочерк
 */
export const formatTransactionTimestamp = (ts?: {
  seconds: bigint | string | number
  nanos: number
}): string => {
  if (!ts || !ts.seconds) return '—'
  const date = new Date(Number(ts.seconds) * 1000)
  return date.toLocaleString('ru-RU')
}
